import path from 'node:path';
import { WORK_DIR } from '~/utils/constants';
import { addWorkDir, removeWorkDir } from '~/utils/workDir';

export const extensions = ['ts', 'js', 'cjs', 'mjs', 'tsx', 'jsx', 'svelte', 'vue'];

export class Resolver {
  fileSet = new Set<string>();
  fileTrie = new ResolveTrie();

  addPath(path: string) {
    if (!this.fileSet.has(path)) {
      this.fileSet.add(path);
      this.fileTrie.insert(path);
    }
  }

  removePath(path: string) {
    if (this.fileSet.has(path)) {
      this.fileSet.delete(path);
      this.fileTrie.remove(path);
    }
  }

  resolve(filePath: string, modulePath: string) {
    const currentDir = path.dirname(addWorkDir(filePath));

    // relative
    if (modulePath[0] === '.') {
      const canonical = path.resolve(currentDir, modulePath);

      const file = this._checkPath(canonical);

      if (file) {
        return file;
      }

      return this._checkPath(path.join(canonical, '/index'));
    }

    // absolute
    if (modulePath.startsWith(WORK_DIR + '/')) {
      return this._checkPath(modulePath);
    }

    /**
     * If we are in an alias, instead of resolving the different tsconfig/jsconfig paths and parsing
     * the JSON5 files, we try and search for a unique file that exists based on the module path with the sigil removed.
     * This should work in nearly all cases and be a lot faster, as we don't even have to touch the FS.
     *
     * It should also work in cases where the aliases are not defined in tsconfig (eg. webpack).
     *
     * There will always be configurations that do not work, but there's no way of covering everything without actually
     * running the code.
     */
    if ('$#%&@~!?*/'.includes(modulePath[0])) {
      const partialPath = modulePath.replace(/^[#$%&@~!?*/]+|(\?|!).*$/g, '');

      return this.fileTrie.search(partialPath) || this.fileTrie.search(partialPath + '/index');
    }

    return undefined;
  }

  private _checkPath(testPath: string) {
    if (this.fileSet.has(testPath)) {
      return testPath;
    }

    for (const ext of extensions) {
      const file = `${testPath}.${ext}`;

      if (this.fileSet.has(file)) {
        return file;
      }
    }

    return undefined;
  }
}

interface Node {
  children: Map<string, Node>;
  extensions?: Set<string>;
  leaf?: string;
}

/**
 * If the target path is `@/foo/bar/index`, we want to search for matches with index, then matches with bar/index, then
 * matches with foo/bar/index until we get a unique path that must be the correct resolution.
 *
 * Doing this by searching through the full file list will be slow and scale poorly. We're running this on the main
 * thread (currently) so it needs to be fast.
 *
 * To avoid iterating, we use a trie structure for fast lookups of each route.
 *
 * Since we are searching from the filename first we reverse the order of the path fragments so we don't have to first iterate
 * through all leaves. This increases the amount of nodes slightly but is much faster than iterating.
 *
 * Potential future perf work: We can trim the trie for paths we know only have a single route, and place the leaf earlier.
 * We would need to regenerate the trimmed branches and retrim on insertion.
 */
export class ResolveTrie {
  root: Node;
  extensions: string[];

  constructor() {
    this.root = {
      children: new Map(),
    };

    this.extensions = ['', ...extensions.map((ext) => `.${ext}`)];
  }

  insert(path: string): void {
    if (path.length === 0) {
      return;
    }

    const [segments, ext] = splitPath(removeWorkDir(path));

    if (ext && !this.extensions.includes(ext)) {
      return;
    }

    let current = this.root;

    for (const segment of segments) {
      if (!current.children.has(segment)) {
        current.children.set(segment, {
          children: new Map(),
        });
      }

      current = current.children.get(segment)!;
    }

    if (!current.leaf) {
      current.leaf = segments.reverse().join('/');
      current.extensions = new Set();
    }

    current.extensions!.add(ext);
  }

  remove(path: string): void {
    const [segments, ext] = splitPath(removeWorkDir(path));

    if (!segments?.length || (ext && !this.extensions.includes(ext))) {
      return;
    }

    let current = this.root;

    const stack: [Node, string][] = [];

    for (const segment of segments) {
      const next = current.children.get(segment);

      if (!next) {
        return;
      }

      stack.push([current, segment]);
      current = next;
    }

    if (!current.extensions?.delete(ext)) {
      return;
    }

    if (current.extensions.size === 0) {
      delete current.leaf;
      delete current.extensions;

      while (stack.length && !current.children.size) {
        const [parent, segment] = stack.pop()!;
        parent.children.delete(segment);
        current = parent;
      }
    }
  }

  search(partialPath: string): string | undefined {
    const [segments, ext] = splitPath(partialPath);

    if (!segments?.length) {
      return undefined;
    }

    let current = this.root;

    const matchingLeaves: Node[] = [];

    /**
     * Because the paths are reversed and because of which paths a user will try and resolve,
     * at the point of calling collectPaths it is almost certain that there is one valid route here.
     *
     * If the user makes a mistake when importing a path and matches multiple routes, we stop recursing.
     */
    const collectPaths = (node: Node) => {
      if (matchingLeaves.length > 1) {
        return;
      }

      if (node.leaf && (!ext || node.extensions!.has(ext))) {
        matchingLeaves.push(node);
      }

      for (const childNode of node.children.values()) {
        collectPaths(childNode);
      }
    };

    for (const segment of segments) {
      if (current.children.has(segment)) {
        current = current.children.get(segment)!;
        continue;
      }

      return undefined;
    }

    collectPaths(current);

    if (matchingLeaves.length === 1) {
      const [match] = matchingLeaves;

      if (ext) {
        return addWorkDir(match.leaf + ext);
      }

      const extension = this.extensions.find((ext) => match.extensions!.has(ext)) ?? '';

      return addWorkDir(match.leaf + extension);
    }

    return undefined;
  }
}

function splitPath(path: string) {
  const trimmed = path[0] === '/' ? path.slice(1) : path;
  const parts = trimmed.split('/');
  const filename = parts.pop() ?? '';
  const extIndex = filename.lastIndexOf('.');
  const hasExtension = extIndex >= 0;

  let extension: string = '';

  if (hasExtension) {
    parts.push(filename.slice(0, extIndex));
    extension = filename.slice(extIndex);
  } else {
    parts.push(filename);
  }

  return [parts.reverse(), extension] as const;
}

export function isScript(path: string) {
  // also support no extension
  if (!path.slice(path.includes('/') ? path.lastIndexOf('/') : 0).includes('.')) {
    return true;
  }

  return extensions.includes(path.slice(path.lastIndexOf('.') + 1));
}
